import { useMemo, useState } from "react";
import {
  Box,
  Button,
  Card,
  Divider,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { OrderSerial } from "models/OrderSerial";
import AddOrderSerialDialog from "dialogs/AddOrderSerialDialog";
import AddIcon from "@mui/icons-material/Add";
import CreateOrderSerialDialog from "dialogs/CreateOrderSerialDialog";
import InstalledIcon from "@mui/icons-material/CheckCircleOutline";
import DoaIcon from "@mui/icons-material/HighlightOffOutlined";
import BoxIcon from "@mui/icons-material/Inventory2";
import Section from "components/Section";
import OutlinedCard from "components/OutlinedCard";
import DataLabel from "components/DataLabel";
import SectionHeader from "components/SectionHeader";
import PageLayout from "components/PageLayout";
import Breadcrumbs from "features/breadcrumbs/Breadcrumbs";
import useAssertParams from "hooks/useAssertParams";
import { useOrderContext } from "context/OrderContext";
import ErrorPage from "pages/error/ErrorPage";

export type OrderRowPageParams = { rowId: string };

const OrderRowPage: React.FC = () => {
  const { rowId } = useAssertParams<OrderRowPageParams>("rowId");
  const order = useOrderContext();

  const [showAddSerial, setShowAddSerial] = useState(false);
  const [showCreateNewSerial, setShowCreateNewSerial] = useState(false);
  const [selectedSerial, setSelectedSerial] = useState<OrderSerial | undefined>();

  const productRow = useMemo(() => {
    if (!rowId) return undefined;
    return order.productRows.find((row) => row.rowId === parseInt(rowId));
  }, [order, rowId]);

  const sortedSerials = useMemo(() => {
    if (!productRow?.serials) return [];
    const serialsToSort = [...productRow.serials];
    return serialsToSort.sort(
      (a, b) =>
        // Uninstalled first, then installed, then DOA
        Number(a.doa) - Number(b.doa) || Number(a.installed) - Number(b.installed)
    );
  }, [productRow]);

  const isAtMaxSerials = useMemo(
    () => (productRow?.serials.length ?? 0) >= (productRow?.quantityDelivered ?? 0),
    [productRow]
  );

  const handleSelectSerial = (serial: OrderSerial) => {
    setSelectedSerial(serial);
    setShowAddSerial(true);
  };

  const handleCreateNewSerial = () => {
    setShowCreateNewSerial(true);
  };

  const renderStatusIcon = (serial: OrderSerial) => {
    if (serial.doa) return <DoaIcon color="error" />;
    if (serial.installed) return <InstalledIcon color="success" />;
    return <AddIcon />;
  };

  if (!productRow) return <ErrorPage.NotFound />;

  return (
    <PageLayout>
      <Breadcrumbs />

      <Card elevation={3} sx={{ mb: 2 }}>
        <Box p={2}>
          <BoxIcon sx={{ color: "#808080", fontSize: "4rem", float: "left", mr: 2 }} />

          <Typography variant="h2" fontSize="1.5rem" fontWeight="bold" mb={1}>
            {productRow?.item}
          </Typography>

          <Typography>{productRow?.description}</Typography>
        </Box>

        <Divider color="grey" />

        <Stack
          direction="row"
          divider={<Divider orientation="vertical" flexItem />}
          spacing={2}
          p={2}
          py={1}
        >
          <DataLabel label="Budgeterat">{productRow?.quantityOrdered ?? 0}</DataLabel>
          <DataLabel label="Utlevererat">{productRow?.quantityDelivered ?? 0}</DataLabel>
          <DataLabel label="Rest">{productRow?.quantityRemaining ?? 0}</DataLabel>
          <DataLabel label="Serienummerbelagd">
            {productRow?.serialRequired ? "Ja" : "Nej"}
          </DataLabel>
        </Stack>
      </Card>

      <Section>
        <SectionHeader title="Serienummer">
          <Tooltip
            arrow
            title={isAtMaxSerials ? "Kan inte lägga till fler serienummer än antal utleverade" : ""}
          >
            <span>
              <Button
                variant="contained"
                disabled={isAtMaxSerials}
                onClick={() => handleCreateNewSerial()}
                startIcon={<AddIcon />}
              >
                Nytt serienummer
              </Button>
            </span>
          </Tooltip>
        </SectionHeader>

        <OutlinedCard p={0}>
          {sortedSerials.length > 0 ? (
            <List sx={{ py: 0 }}>
              {sortedSerials.map((serial) => (
                <ListItemButton
                  key={serial.serial}
                  onClick={() => handleSelectSerial(serial)}
                  disabled={serial.installed || serial.doa}
                  divider={true}
                >
                  <ListItemText>{serial.serial}</ListItemText>

                  <ListItemIcon sx={{ justifyContent: "flex-end" }}>
                    {renderStatusIcon(serial)}
                  </ListItemIcon>
                </ListItemButton>
              ))}
            </List>
          ) : (
            <Typography m={1}>Inga serienummer registrerade</Typography>
          )}
        </OutlinedCard>
      </Section>

      {showAddSerial && (
        <AddOrderSerialDialog serial={selectedSerial!} onClose={() => setShowAddSerial(false)} />
      )}

      {showCreateNewSerial && (
        <CreateOrderSerialDialog
          productRow={productRow}
          onClose={() => setShowCreateNewSerial(false)}
        />
      )}
    </PageLayout>
  );
};

export default OrderRowPage;
